import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';
import React from 'react';
import { C, soft, GRADIENT_BRAND } from '@parsina/framework/tokens';
import { Card, TopBar, useKeyboardInset } from '@parsina/framework';
import { bffQuery, bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';
import { usePatient } from '@parsina/framework/clinical/src/hooks/usePatient';
import { useTemplates } from '../../hooks/useTemplates';

const UPDATE_VISIT = `
  mutation UpdateVisit($id: ID!, $input: VisitInput!) {
    updateVisit(id: $id, input: $input) { id status }
  }
`;

// createMedication uses MedicationInput (dosageInstruction: JSON) — fixes the
// double-encoding bug of the old addMedication (dosageInstruction: String).
const CREATE_MEDICATION = `mutation CreateMedication($patientId:ID!,$input:MedicationInput!){createMedication(patientId:$patientId,input:$input){id}}`;

const CHECK_DRUG_INTERACTION = `
  query CheckDrugInteraction($medicationIds: [String!]!, $patientId: ID!, $patientConditions: [String!], $patientAllergies: [String!]) {
    checkDrugInteraction(medicationIds: $medicationIds, patientId: $patientId, patientConditions: $patientConditions, patientAllergies: $patientAllergies) {
      hasInteractions
      warnings { severity description drugPair }
    }
  }
`;

const ASSIST_PRESCRIPTION = `
  mutation AssistPrescription($input: AssistPrescriptionInput!) {
    assistPrescription(input: $input) {
      hasInteractions
      interactionWarnings { severity description drugPair }
      suggestedMedications { name dosage frequency duration notes }
      recommendedTests { testName reason urgency }
      missingTests
      followUpTimeline
    }
  }
`;

// i18n-exempt: the Iranian drug formulary. A medication's name is what the
// medicine is called — it is the same word in an English UI, it is what a
// pharmacy dispenses against, and it is the value written to the
// prescription. Translating it would be a clinical error, not a feature.
// Latin generics, like every other drug name in the app — see lib/drugNames.js.
// This short list is a stand-in for the formulary, and the name a doctor picks
// off it is the name that is stored, shown and sent for the interaction check,
// so it has to be the same kind of name the formulary would have given them.
const DRUG_LIST = [
  'Amoxicillin','Ibuprofen','Losartan','Atorvastatin','Omeprazole',
  'Metformin','Aspirin','Ciprofloxacin','Levothyroxine','Amlodipine',
  'Esomeprazole','Pantoprazole','Sertraline','Fluoxetine','Propranolol',
  'Diclofenac','Naproxen','Prednisolone','Azithromycin','Clarithromycin',
];

const PrescriptionMobile = ({ navigate, patient: patientProp }) => {
  const keyboardInset = useKeyboardInset();
  const { t, formatList } = useTranslation();
  const p = patientProp || { name:t('patient.sampleName'), insurance:'military', id: 'v1' };
  const patientId = p.patientId || p.id;
  const visitId = p.visitId || 'v1';
  const { patient: patientFull } = usePatient(patientId);
  const { prescriptionTemplates } = useTemplates();

  // `usePatient` returns clinicalAllergies / clinicalConditions — reading the
  // old names left both empty on a deep link, so the allergy banner vanished and
  // the interaction check ran without the patient's allergies. Router state is
  // only the fallback while the record loads.
  const allergies   = patientFull?.clinicalAllergies  || p.allergies  || [];
  const conditions  = patientFull?.clinicalConditions || p.conditions || [];

  // A prescription starts EMPTY. It used to open with a seeded «متفورمین»
  // row, which a submit then wrote into the patient's record.
  const [meds, setMeds] = React.useState([]);
  const [showAdd, setShowAdd]       = React.useState(false);
  const [search, setSearch]         = React.useState('');
  const [submitState, setSubmitState] = React.useState('idle');

  const [interactionWarning, setInteractionWarning] = React.useState(null);
  const [checkingInteraction, setCheckingInteraction] = React.useState(false);
  const [interactionCheckFailed, setInteractionCheckFailed] = React.useState(false);

  const [aiSuggest, setAiSuggest] = React.useState(null);
  const [aiLoading, setAiLoading] = React.useState(false);

  const filtered = search
    ? DRUG_LIST.filter(d => d.toLowerCase().includes(search.trim().toLowerCase()))
    : DRUG_LIST.slice(0, 8);

  const normalizedAllergies = allergies.map(a => typeof a === 'string' ? a : a.allergen).filter(Boolean);
  // The API takes names, not condition objects.
  const normalizedConditions = conditions
    .map(c => typeof c === 'string' ? c : (c.display || c.code))
    .filter(Boolean);

  const checkInteractions = async (medNames) => {
    if (medNames.length < 2) return;
    setCheckingInteraction(true);
    try {
      const data = await bffQuery(CHECK_DRUG_INTERACTION, {
        medicationIds: medNames,
        patientId: patientId || 'unknown',
        patientConditions: normalizedConditions,
        patientAllergies: normalizedAllergies,
      });
      setInteractionCheckFailed(false);
      const result = data.checkDrugInteraction;
      if (result.hasInteractions && result.warnings?.length > 0) {
        setInteractionWarning(result.warnings);
      } else {
        setInteractionWarning(null);
      }
    } catch {
      // Never blocks prescribing — but a check that did not run must not look
      // like a check that found nothing.
      setInteractionCheckFailed(true);
    } finally {
      setCheckingInteraction(false);
    }
  };

  const addMed = (name) => {
    const newMed = { id: Date.now(), name, dose: '', freq: t('visit.rx.freq.od'), duration: t('credits.range.days30') };
    const updated = [...meds, newMed];
    setMeds(updated);
    setShowAdd(false);
    setSearch('');
    checkInteractions(updated.map(m => m.name));
  };

  const removeMed = (id) => {
    const updated = meds.filter(x => x.id !== id);
    setMeds(updated);
    if (updated.length >= 2) checkInteractions(updated.map(m => m.name));
    else setInteractionWarning(null);
  };

  const handleAiSuggest = async () => {
    setAiLoading(true);
    setAiSuggest(null);
    try {
      const data = await bffMutation(ASSIST_PRESCRIPTION, {
        input: {
          visitId: visitId || 'unknown',
          patientId: patientId || 'unknown',
          medications: meds.map(m => m.name),
          conditions: normalizedConditions,
          age: patientFull?.age || p.age || 0,
          gender: patientFull?.gender || p.gender || '',
          patientAllergies: normalizedAllergies,
        },
      });
      setAiSuggest(data.assistPrescription);
    } catch {
      setAiSuggest({ error: true });
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = () => {
    if (submitState === 'idle') { setSubmitState('confirm'); return; }
    if (submitState === 'confirm') {
      setSubmitState('sending');
      const today = new Date().toISOString().split('T')[0];
      // UPDATE_VISIT no longer accepts `prescription` — that field was removed from
      // VisitInput in the FHIR refactor.  Save each medication via createMedication
      // (which persists it properly), then mark the visit visited.
      const medSaves = patientId
        ? meds.map(m =>
            bffMutation(CREATE_MEDICATION, {
              patientId,
              input: {
                code: m.name,
                display: m.name,
                status: 'active',
                authoredOn: today,
                dosageInstruction: {
                  text: [m.dose, m.freq, m.duration ? t('visit.rx.forDuration', { duration: m.duration }) : '']
                    .filter(Boolean)
                    .join(' '),
                },
                appointmentId: visitId || undefined,
              },
            })
          )
        : [];
      Promise.all(medSaves)
        .then(() =>
          bffMutation(UPDATE_VISIT, { id: visitId, input: { status: 'visited' } })
        )
        .then(() => setSubmitState('done'))
        .catch(() => setSubmitState('idle'));
    }
  };

  const freqs = [t('visit.rx.freq.od'),t('visit.rx.freq.bd'),t('visit.rx.freq.tds'),t('visit.rx.freq.prn')];
  const insLabel = p.insurance === 'military' ? t('patient.insurance.military') : t('patient.insurance.social');
  const insColor = p.insurance === 'military'
    ? { bg:soft(C.navy,0.08), text:C.navyDark, border:soft(C.navy,0.25) }
    : { bg:'#F0FDF4', text:'#065F46', border:'#BBF7D0' };

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',background:C.bg,overflowY:'auto'}}>
      <TopBar title={t('nav.items.prescription')} subtitle={p.name} onBack={()=>navigate('visit',p)}/>

      <div style={{padding:'12px 16px 0'}}>

        {/* The insurer is an Iranian one (نیروهای مسلح / تأمین اجتماعی) and the
            «sent online» line is the Iranian e-prescription, so outside the
            Iranian market (`market: 'intl'`) the banner is not shown. */}
        {isIranMarket() && (
        <div style={{background:insColor.bg,border:`1px solid ${insColor.border}`,borderRadius:12,padding:'10px 14px',display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
          <svg viewBox="0 0 24 24" fill={insColor.text} width="20" height="20"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l6 2.67V11c0 3.71-2.54 7.21-6 8.29-3.46-1.08-6-4.58-6-8.29V7.67L12 5z"/></svg>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:insColor.text}}>{insLabel}</div>
            <div style={{fontSize:11,color:insColor.text,opacity:0.8}}>{t('prescription.sentOnline')}</div>
          </div>
        </div>
        )}

        {allergies.length > 0 && (
          <div style={{background:C.redLight,border:'1px solid #FCA5A5',borderRadius:12,padding:'10px 14px',display:'flex',gap:8,alignItems:'center',marginBottom:12}}>
            <span style={{fontSize:16}}>⚠️</span>
            <span style={{fontSize:12,color:C.red,fontWeight:600}}>{t('prescription.recordedAllergy')} {formatList(normalizedAllergies)} {t('prescription.doNotPrescribe')}</span>
          </div>
        )}

        {checkingInteraction && (
          <div style={{background:'#FFF7ED',border:'1px solid #FED7AA',borderRadius:12,padding:'10px 14px',fontSize:12,color:'#92400E',marginBottom:12,display:'flex',gap:8,alignItems:'center'}}>
            <div style={{width:14,height:14,border:'2px solid #92400E',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 1s linear infinite',flexShrink:0}}/>
            {t('visit.rx.checkingInteractions')}
          </div>
        )}

        {!checkingInteraction && interactionCheckFailed && (
          <div role="alert" data-testid="rx-interaction-check-failed" style={{background:C.amberLight,border:`1px solid ${C.amber}`,borderRadius:12,padding:'10px 14px',fontSize:12,color:C.text,marginBottom:12,display:'flex',gap:8,alignItems:'center',justifyContent:'space-between'}}>
            <span>{t('prescription.interactionCheckFailed')}</span>
            <button type="button" onClick={() => checkInteractions(meds.map(m => m.name))} style={{background:'none',border:'none',cursor:'pointer',fontWeight:700,color:C.navy,fontFamily:'inherit',fontSize:12,flexShrink:0}}>{t('common.actions.retry')}</button>
          </div>
        )}

        {!checkingInteraction && interactionWarning?.length > 0 && (
          <div style={{background:'#FFF7ED',border:'1.5px solid #FED7AA',borderRadius:14,padding:'14px',marginBottom:12,position:'relative'}}>
            <button onClick={()=>setInteractionWarning(null)} style={{position:'absolute',top:10,left:12,background:'none',border:'none',cursor:'pointer',fontSize:16,color:C.textMuted}}>✕</button>
            <div style={{display:'flex',gap:10,alignItems:'flex-start'}}>
              <span style={{fontSize:20}}>🚨</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:'#9A3412',marginBottom:8}}>{t('prescription.interactionWarning')}</div>
                {interactionWarning.map((w, i) => {
                  return (
                  <div key={i} style={{fontSize:12,color:'#C2410C',marginBottom:4,paddingInlineStart:8,borderInlineStart:'2px solid #FED7AA'}}>
                    <span style={{fontWeight:700,background:'#FED7AA',padding:'1px 5px',borderRadius:4,marginInlineEnd:6,fontSize:10}}>{w.severity === 'high' ? t('visit.vitals.high') : w.severity === 'medium' ? t('credits.level.midShort') : t('visit.vitals.low')}</span>
                    {w.description}
                  </div>
                );
                                        })}
              </div>
            </div>
          </div>
        )}

        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <div style={{fontSize:15,fontWeight:700,color:C.textPrimary}}>{t('prescription.medicationsPrefix')}{meds.length})</div>
          <button onClick={()=>{setShowAdd(!showAdd);setSearch('');}} style={{
            display:'flex',alignItems:'center',gap:6,padding:'7px 12px',
            background:C.navy,color:'white',border:'none',borderRadius:10,fontSize:12,fontWeight:600,cursor:'pointer',
          }}>
            <svg viewBox="0 0 24 24" fill="white" width="14" height="14"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/></svg>
            {t('patient.meds.add')}
          </button>
        </div>

        {showAdd && (
          <Card style={{padding:'14px',marginBottom:12,border:`1.5px solid ${C.navy}`}}>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder={t('prescription.searchDrug')}
              style={{width:'100%',height:42,border:`1.5px solid ${C.border}`,borderRadius:10,padding:'0 12px',fontSize:13,fontFamily: 'var(--app-font)',outline:'none',background:'#fafbfc',marginBottom:8}}
            />
            <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:200,overflowY:'auto'}}>
              {filtered.map((name, i) => (
                <button key={i} onClick={()=>addMed(name)} style={{
                  padding:'10px 12px',borderRadius:10,border:`1px solid ${C.border}`,
                  background:'white',cursor:'pointer',textAlign:'start',
                  display:'flex',alignItems:'center',gap:10,
                }}>
                  <span style={{fontSize:16}}>💊</span>
                  <span style={{fontSize:13,fontWeight:600,color:C.textPrimary}}>{name}</span>
                </button>
              ))}
              {search && !DRUG_LIST.includes(search) && (
                <button onClick={()=>addMed(search)} style={{
                  padding:'10px 12px',borderRadius:10,border:`1.5px dashed ${C.navy}`,
                  background:soft(C.navy,0.06),cursor:'pointer',textAlign:'start',
                  display:'flex',alignItems:'center',gap:10,
                }}>
                  <span style={{fontSize:16}}>➕</span>
                  <span style={{fontSize:13,fontWeight:600,color:C.navy}}>{t('prescription.addPrefix')}{search}»</span>
                </button>
              )}
            </div>
          </Card>
        )}

        <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:12}}>
          {meds.map((med) => (
            <Card key={med.id} style={{padding:'14px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                <div style={{display:'flex',gap:10,alignItems:'center'}}>
                  <div style={{width:40,height:40,background:soft(C.navy,0.08),borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>💊</div>
                  <div>
                    <div dir="auto" style={{fontSize:14,fontWeight:700,color:C.textPrimary}}>{med.name}</div>
                    <input value={med.dose} onChange={e=>setMeds(m=>m.map(x=>x.id===med.id?{...x,dose:e.target.value}:x))}
                      placeholder={t('prescription.dosePlaceholder')}
                      style={{fontSize:11,color:C.textMuted,border:'none',background:'none',outline:'none',fontFamily: 'var(--app-font)',marginTop:2,width:'100%'}}/>
                  </div>
                </div>
                <button onClick={()=>removeMed(med.id)} style={{background:'none',border:'none',cursor:'pointer',color:C.textMuted,fontSize:18,lineHeight:1,padding:'2px 4px'}}>✕</button>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                <div>
                  <div style={{fontSize:11,color:C.textMuted,marginBottom:4}}>{t('prescription.frequency')}</div>
                  <select value={med.freq} onChange={e=>setMeds(m=>m.map(x=>x.id===med.id?{...x,freq:e.target.value}:x))}
                    style={{width:'100%',height:36,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,fontFamily: 'var(--app-font)',padding:'0 8px',outline:'none',background:'#fafbfc',color:C.textPrimary}}>
                    {freqs.map(f=><option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:11,color:C.textMuted,marginBottom:4}}>{t('prescription.duration')}</div>
                  <input value={med.duration} onChange={e=>setMeds(m=>m.map(x=>x.id===med.id?{...x,duration:e.target.value}:x))}
                    style={{width:'100%',height:36,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,fontFamily: 'var(--app-font)',padding:'0 8px',outline:'none',background:'#fafbfc',color:C.textPrimary}}/>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {prescriptionTemplates.length > 0 && (
          <Card style={{padding:'14px',background:soft(C.navy,0.08),border:`1px solid ${soft(C.navy,0.25)}`,marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:C.navyDark,marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
              <svg viewBox="0 0 24 24" fill={C.navyDark} width="14" height="14"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
              {t('prescription.templates')}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {prescriptionTemplates.map(tpl=>(
                <div key={tpl.id} style={{padding:'8px 10px',background:'white',borderRadius:10,border:`1px solid ${soft(C.navy,0.25)}`}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.navyDark,marginBottom:4}}>💊 {tpl.title}</div>
                  {tpl.content && (
                    <div style={{fontSize:11,color:C.textSecondary,lineHeight:1.6,whiteSpace:'pre-line'}}>{tpl.content}</div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        <button onClick={handleAiSuggest} disabled={aiLoading} style={{
          width:'100%',height:44,marginBottom:12,
          background:aiLoading?C.border:GRADIENT_BRAND,
          color:'white',border:'none',borderRadius:12,fontSize:13,fontWeight:600,cursor:'pointer',
          display:'flex',alignItems:'center',justifyContent:'center',gap:8,
        }}>
          {aiLoading ? (
            <>
              <div style={{width:16,height:16,border:'2px solid white',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
              {t('prescription.analysing')}
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
              {t('prescription.aiSuggestTitle')}
            </>
          )}
        </button>

        {aiSuggest && !aiSuggest.error && (
          <Card style={{padding:'14px',marginBottom:12,border:`1.5px solid ${C.teal}`}}>
            <div style={{fontSize:13,fontWeight:700,color:C.teal,marginBottom:12,display:'flex',alignItems:'center',gap:6}}>
              <svg viewBox="0 0 24 24" fill={C.teal} width="16" height="16"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
              {t('prescription.aiSuggestion')}
            </div>

            {aiSuggest.interactionWarnings?.length > 0 && (
              <div style={{background:'#FFF7ED',borderRadius:10,padding:'10px',marginBottom:10}}>
                <div style={{fontSize:11,fontWeight:700,color:'#9A3412',marginBottom:6}}>{t('prescription.interactionsFound')}</div>
                {aiSuggest.interactionWarnings.map((w,i)=>(
                  <div key={i} style={{fontSize:11,color:'#C2410C',marginBottom:3}}>• {w.description}</div>
                ))}
              </div>
            )}

            {aiSuggest.suggestedMedications?.length > 0 && (
              <div style={{marginBottom:10}}>
                <div style={{fontSize:11,fontWeight:700,color:C.textSecondary,marginBottom:6}}>{t('prescription.suggestedDrugs')}</div>
                {aiSuggest.suggestedMedications.map((m,i)=>(
                  <div key={i} style={{background:soft(C.navy,0.06),borderRadius:8,padding:'8px 10px',marginBottom:6}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.textPrimary}}>{m.name}</div>
                    <div style={{fontSize:11,color:C.textSecondary,marginTop:2}}>{[m.dosage,m.frequency,m.duration].filter(Boolean).join(' — ')}</div>
                    {m.notes && <div style={{fontSize:10,color:C.textMuted,marginTop:2}}>{m.notes}</div>}
                    <button onClick={()=>addMed(m.name)} style={{
                      marginTop:6,padding:'4px 10px',borderRadius:8,border:`1px solid ${C.navy}`,
                      background:'white',color:C.navy,fontSize:10,fontWeight:600,cursor:'pointer',
                    }}>{t('prescription.addToRx')}</button>
                  </div>
                ))}
              </div>
            )}

            {aiSuggest.recommendedTests?.length > 0 && (
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.textSecondary,marginBottom:6}}>{t('prescription.suggestedTests')}</div>
                {aiSuggest.recommendedTests.map((order,i)=>(
                  <div key={i} style={{background:soft(C.navy,0.06),borderRadius:8,padding:'8px 10px',marginBottom:6}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontSize:12,fontWeight:600,color:C.textPrimary}}>{order.testName}</span>
                      <span style={{fontSize:10,background:order.urgency==='urgent'?C.redLight:soft(C.navy,0.08),color:order.urgency==='urgent'?C.red:C.navyDark,padding:'2px 6px',borderRadius:6,fontWeight:600}}>
                        {order.urgency==='urgent'?t('common.urgency.urgent'):order.urgency==='soon'?t('prescription.sooner'):t('prescription.normal')}
                      </span>
                    </div>
                    {order.reason && <div style={{fontSize:11,color:C.textMuted,marginTop:2}}>{order.reason}</div>}
                  </div>
                ))}
              </div>
            )}

            {aiSuggest.followUpTimeline && (
              <div style={{marginTop:8,padding:'8px 10px',background:'#f8f9fb',borderRadius:8,fontSize:11,color:C.textSecondary}}>
                {t('prescription.followUp')} {aiSuggest.followUpTimeline}
              </div>
            )}
          </Card>
        )}

        {aiSuggest?.error && (
          <div style={{background:C.redLight,borderRadius:12,padding:'10px 14px',fontSize:12,color:C.red,marginBottom:12}}>{t('prescription.suggestFailed')}</div>
        )}

      </div>

      {/* §H.1 — the docked submit clears the software keyboard and the home
          indicator. Adding a drug puts the keyboard up on this very screen. */}
      <div style={{padding:`16px 20px calc(16px + env(safe-area-inset-bottom, 0px) + ${keyboardInset}px)`,borderTop:`1px solid ${C.border}`,background:'white',marginTop:'auto'}}>
        <button onClick={handleSubmit} disabled={meds.length===0 || submitState==='sending'} style={{
          width:'100%',height:52,
          background:submitState==='done'?C.green:`linear-gradient(135deg,${C.navy},${C.navyLight})`,
          color:'white',border:'none',borderRadius:14,fontSize:16,fontWeight:700,cursor:'pointer',
          boxShadow:`0 4px 16px ${soft(C.navy,0.3)}`,
          display:'flex',alignItems:'center',justifyContent:'center',gap:10,
        }}>
          {submitState==='idle' && t('prescription.sendOnline')}
          {submitState==='confirm' && t('prescription.confirmSend')}
          {submitState==='sending' && (
            <>
              <div style={{width:18,height:18,border:'3px solid white',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
              {t('auth.login.sending')}
            </>
          )}
          {submitState==='done' && t('prescription.sent')}
        </button>
      </div>
    </div>
  );
};

export default PrescriptionMobile;
